/* ============================================================================
   Piezas pequeñas del sistema: tarjeta, sección, callout, chip, fila de filtros,
   número héroe y chip de procedencia.
   ========================================================================== */

import { createContext, useContext, useId } from 'react'
import type { Confidence } from '../data/schema'
import { useSpringNumber } from '../lib/motion'
import './ui.css'

/* --- Tarjeta ------------------------------------------------------------- */

export function Card({
  children,
  className = '',
  padded = true,
}: {
  children: React.ReactNode
  className?: string
  padded?: boolean
}) {
  return (
    <section className={`card${padded ? ' card--padded' : ''} ${className}`.trim()}>{children}</section>
  )
}

/* --- Sección con encabezado --------------------------------------------- */

export function Section({
  title,
  description,
  actions,
  children,
  id,
}: {
  title: string
  description?: string
  actions?: React.ReactNode
  children: React.ReactNode
  id?: string
}) {
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, '')
  const headingId = id ?? `${uid}-h`
  return (
    <section className="section" aria-labelledby={headingId}>
      <header className="section__head">
        <div className="section__titles">
          <h2 className="t-title1" id={headingId}>
            {title}
          </h2>
          {description ? <p className="t-body ink-2 section__desc">{description}</p> : null}
        </div>
        {actions ? <div className="section__actions">{actions}</div> : null}
      </header>
      {children}
    </section>
  )
}

/* --- Callout ------------------------------------------------------------- */

export type CalloutKind = 'info' | 'warning' | 'critical'

const CALLOUT_ICON: Record<CalloutKind, string> = {
  info: 'i',
  warning: '!',
  critical: '!',
}

const CALLOUT_ROLE: Record<CalloutKind, string> = {
  info: 'Nota',
  warning: 'Advertencia',
  critical: 'Importante',
}

/**
 * Los tokens de estado nunca cargan el sentido solos: el callout siempre lleva
 * icono + etiqueta de rol.
 */
export function Callout({
  kind = 'info',
  title,
  children,
}: {
  kind?: CalloutKind
  title?: string
  children: React.ReactNode
}) {
  return (
    <aside className={`callout callout--${kind}`}>
      <span className="callout__icon" aria-hidden="true">
        {CALLOUT_ICON[kind]}
      </span>
      <div className="callout__body">
        <p className="callout__role t-micro">
          {CALLOUT_ROLE[kind]}
          {title ? <span className="callout__title"> · {title}</span> : null}
        </p>
        <div className="t-footnote ink-2 callout__text">{children}</div>
      </div>
    </aside>
  )
}

/* --- Chip ---------------------------------------------------------------- */

export function Chip({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode
  tone?: 'neutral' | 'accent'
}) {
  return <span className={`chip chip--${tone} t-caption`}>{children}</span>
}

/* --- Chip de procedencia ------------------------------------------------- */

const CONFIDENCE_LABEL: Record<Confidence, string> = {
  high: 'verificado',
  medium: 'derivado',
  low: 'sin verificar',
}

export function SourceChip({
  label,
  confidence,
  onClick,
}: {
  label: string
  confidence?: Confidence
  onClick?: () => void
}) {
  const content = (
    <>
      <span className="source-chip__dot" aria-hidden="true" />
      <span className="source-chip__label">{label}</span>
      {confidence ? (
        <span className={`source-chip__conf source-chip__conf--${confidence}`}>
          {CONFIDENCE_LABEL[confidence]}
        </span>
      ) : null}
    </>
  )
  if (!onClick) return <span className="source-chip t-caption">{content}</span>
  return (
    <button type="button" className="source-chip source-chip--button t-caption" onClick={onClick}>
      {content}
    </button>
  )
}

/* --- Fila de filtros ---------------------------------------------------- */

/** Una sola fila, arriba de todo lo que filtra. Nunca dentro de una gráfica. */
export function FilterBar({
  children,
  ariaLabel,
}: {
  children: React.ReactNode
  ariaLabel: string
}) {
  return (
    <div className="filterbar" role="group" aria-label={ariaLabel}>
      {children}
    </div>
  )
}

const FilterLabelContext = createContext<string | null>(null)

/** Id del rótulo del filtro que envuelve al control, si hay uno. */
export function useFilterLabelId(): string | null {
  return useContext(FilterLabelContext)
}

/**
 * Rótulo de un filtro.
 *
 * NO es un `<label>`. Un `<label>` que envuelve un `Segmented` toma como control
 * etiquetado su primer `<button role="radio">` —los botones son etiquetables
 * según el HTML Standard—, así que hacer clic en el texto del rótulo disparaba un
 * clic sintético sobre la PRIMERA opción: tocar la palabra «Ámbito» cambiaba la
 * vista de rural a urbano. Además el `<label>` no podía asociarse con el
 * `role="radiogroup"`, o sea que ni siquiera cumplía su función.
 *
 * Ahora es un `<div>` con un id que viaja por contexto al control, que se nombra
 * con `aria-labelledby`: el nombre accesible contiene el texto visible.
 */
export function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, '')
  const labelId = `${uid}-filtro`
  return (
    <FilterLabelContext.Provider value={labelId}>
      <div className="filterfield">
        <span className="filterfield__label t-micro ink-3" id={labelId}>
          {label}
        </span>
        {children}
      </div>
    </FilterLabelContext.Provider>
  )
}

/* --- Número héroe ------------------------------------------------------- */

const HERO_SPRING = { damping: 1, response: 0.32 } as const

/** Exactamente uno por vista. Cifras proporcionales, misma tipografía sans. */
export function HeroFigure({
  value,
  format,
  caption,
  unit,
  children,
  immediate = false,
}: {
  value: number
  format: (value: number) => string
  caption: string
  unit?: string
  children?: React.ReactNode
  /** Sin animacion: obligatorio mientras el lector arrastra el cursor. */
  immediate?: boolean
}) {
  const animated = useSpringNumber(value, HERO_SPRING, immediate)
  return (
    <div className="hero">
      <p className="hero__caption t-footnote ink-2">{caption}</p>
      <p className="hero__value t-hero">
        {format(animated)}
        {unit ? <span className="hero__unit"> {unit}</span> : null}
      </p>
      {children ? <div className="hero__meta">{children}</div> : null}
    </div>
  )
}

/* --- Select del sistema -------------------------------------------------- */

export function Select<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  ariaLabelledBy,
}: {
  value: T
  onChange: (value: T) => void
  options: readonly { value: T; label: string }[]
  ariaLabel?: string
  /** Id del rótulo visible. Si no se pasa, se toma del FilterField que envuelve. */
  ariaLabelledBy?: string
}) {
  /* Preferimos el rótulo visible: un aria-label que no contiene el texto que se
     ve rompe «Label in Name» (WCAG 2.5.3) para quien usa control por voz. */
  const contextLabelId = useFilterLabelId()
  const labelledBy = ariaLabelledBy ?? contextLabelId
  return (
    <div className="select">
      <select
        className="select__control t-footnote"
        value={value}
        {...(labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': ariaLabel })}
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg className="select__chevron" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
        <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  )
}

/* --- Estado vacío ------------------------------------------------------- */

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <p className="t-title3">{title}</p>
      <p className="t-footnote ink-2">{body}</p>
    </div>
  )
}
